import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IChildItem, IMenuItem } from '../../services/navigation.service';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SettingsService } from 'src/app/views/settings/settings.service';
import { KycStatusService, KycStatusSnapshot } from '../../services/kyc-status.service';
import { gatePassed } from '../../utils/compliance-exemption.util';
import { ChatbotAiService } from '../../services/chatbot-ai.service';
import { RfqCacheService } from 'src/app/views/dashboard/rfq-cache.service';
import { SUPPLIER_PREVIEW_SCREENS_ENABLED } from '../../supplier-preview-screens';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.css']
})
export class SidenavComponent implements OnInit, OnDestroy {
  @Input('items') public menuItems: IMenuItem[] = [];
  @Input('hasIconMenu')
  public hasIconTypeMenuItem!: boolean;
  @Input('iconMenuTitle') public iconTypeMenuTitle!: string;
  @Input('isCompact') public isCompact: boolean = false;
  @Output() menuClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() expandSidebar: EventEmitter<void> = new EventEmitter<void>();
  isWarningNotification: boolean = false;
  isForceNotification: boolean = false;
  mailCount!:any[];
  kycApproved: boolean = true;
  private kycSub?: Subscription;
  private readonly menuOrder = [
    'dashboard',
    'search',
    'inbox',
    'operations',
    'inventory management',
    'user management',
    'data management',
    'help',
    'notifications',
    'settings',
    'my profile',
    'contact us',
    'sign out'
  ];
  // Buyer manual bumped to V1.1 (converted from the final draft docx and
  // uploaded 31-07-2026; the V1.0.0.1 object stays on S3 as rollback).
  // Author: Akarsh Singh
  private readonly buyerHelpUrl = 'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/VSMPS_Buyer_Manual_V1.1.pdf';
  // Supplier manual bumped to V2.0.0 (converted from the v2 docx and uploaded
  // 01-08-2026; the V1.0.0.1 object stays on S3 as rollback).
  // Author: Akarsh Singh
  private readonly supplierHelpUrl = 'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/VSMPS_Supplier_Manual_V2.0.0.pdf';
  private readonly adminHelpUrl = 'https://vsmps-assets-dev.s3.eu-west-1.amazonaws.com/VSMPS_Admin_Manual_V1.0.0.1.pdf';
  private readonly videoTutorialsUrl = 'https://s3.eu-west-1.amazonaws.com/vsmps.varuna-sentinels.com/assets/VS-Supplier-Demo.mp4';
  private readonly faqsUrl = 'https://vsmps.varuna-sentinels.com/home';
  private userRole = '';
  private supplierMenuSource?: IMenuItem[];
  private supplierMenuItems: IMenuItem[] = [];

  /** Help Center slide-in panel visibility. */
  showHelpCenter = false;

  constructor(
    private cookieService: CookieService,
    private router: Router,
    private settingsService: SettingsService,
    private snack: MatSnackBar,
    private kycStatus: KycStatusService,
    private chatbotAi: ChatbotAiService,
    private rfqCacheService: RfqCacheService
  ) {
    this.isWarningNotification = this.cookieService.check("isWarningNotification") ? JSON.parse(this.cookieService.get("isWarningNotification")) : false;
    this.isForceNotification = this.cookieService.check("isForceNotification") ? JSON.parse(this.cookieService.get("isForceNotification")) : false;
    this.userRole = (this.cookieService.get('userRole') || '').trim().toLowerCase();
    this.addMenuItem();
  }

  ngOnInit(): void {
    this.kycSub = this.kycStatus.status$.subscribe((snap: KycStatusSnapshot) => {
      // Admin role and any non-applicant role bypass gating; status service
      // reports APPROVED for them so the sidebar stays unrestricted.
      this.kycApproved = !this.kycStatus.isApplicantRole() || gatePassed(snap);
    });
  }

  ngOnDestroy(): void {
    this.kycSub?.unsubscribe();
  }

  // Only for demo purpose
  async addMenuItem() {
    this.menuItems.push({
      name: 'ITEM',
      type: 'dropDown',
      tooltip: 'Item',
      icon: 'done',
      state: 'material',
      sub: [
        { name: 'SUBITEM', state: 'cards' },
        { name: 'SUBITEM', state: 'buttons' }
      ],
      action: null
    });

    // Inbox count badge. Cached client-side (short TTL) keyed by user so the
    // count paints instantly on every reload/navigation instead of firing a
    // fresh request each time the sidenav is constructed.
    const userId = Number(this.cookieService.get('userId'));
    this.mailCount = await this.rfqCacheService.getOrFetchLookup(
      'sidenavMailCount',
      userId,
      () => this.settingsService.getTotalMailCount(),
      60 * 1000
    );
  }

  onMenuClick(menu:IMenuItem){
    this.menuClick.emit(menu)
    if (menu.sub) {
      this.router.navigate([`/${menu.state}`]);
    } else {
      this.router.navigate([`/${menu.state}`]);
    }
  }

  handleMenuClick(menu: IMenuItem, event: Event) {
    // If KYC isn't approved and the item isn't on the essentials allow-list,
    // block navigation entirely and surface a snackbar nudging them to KYC.
    if (this.isLocked(menu)) {
      event.preventDefault();
      event.stopPropagation();
      this.snack.open(
        'Complete your KYC verification to access this section.',
        'OK',
        { duration: 3500 }
      );
      return;
    }

    const isHelpMenu = (menu?.name || '').trim().toLowerCase() === 'help';
    if (!isHelpMenu) {
      return;
    }

    // Help no longer opens the PDF straight away — it opens the Help Center
    // panel, which keeps the role-based manual plus support/AI options.
    event.preventDefault();
    event.stopPropagation();
    this.openHelpCenter();
  }

  // ===== Help Center panel =====
  openHelpCenter(): void {
    this.showHelpCenter = true;
  }

  closeHelpCenter(): void {
    this.showHelpCenter = false;
  }

  @HostListener('document:keydown.escape')
  onHelpCenterEscape(): void {
    if (this.showHelpCenter) {
      this.closeHelpCenter();
    }
  }

  /** Role-based User Manual PDF — the original Help behaviour, preserved. */
  openUserManual(): void {
    const pdfUrl =
      this.userRole === 'owner'
        ? this.buyerHelpUrl
        : this.userRole === 'admin'
        ? this.adminHelpUrl
        : this.supplierHelpUrl;
    window.open(`${pdfUrl}#toolbar=1`, '_blank');
    this.closeHelpCenter();
  }

  /** Pop open the in-app AI assistant. */
  openAiAssistant(): void {
    this.chatbotAi.requestOpen();
    this.closeHelpCenter();
  }

  /** Open the in-app Support Ticket System. */
  openContactSupport(): void {
    this.router.navigate(['/operations/support-ticket-system']);
    this.closeHelpCenter();
  }

  /** FAQs — opens the public help/home site in a new tab (secured). */
  openFaqs(): void {
    this.openExternal(this.faqsUrl);
    this.closeHelpCenter();
  }

  /** Video Tutorials — opens the demo video in a new tab (secured). */
  openVideoTutorials(): void {
    this.openExternal(this.videoTutorialsUrl);
    this.closeHelpCenter();
  }

  /**
   * Open an external URL in a new tab with tab-nabbing protection.
   * `noopener` severs window.opener so the opened page can't script this one;
   * `noreferrer` also withholds the Referer header. Only https links are passed.
   */
  private openExternal(url: string): void {
    if (!/^https:\/\//i.test(url)) {
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Placeholder items (What's New) until content exists. */
  helpComingSoon(label: string): void {
    this.snack.open(`${label} — coming soon.`, 'OK', { duration: 2500 });
  }

  handleSubMenuClick(child: IChildItem, event: Event) {
    if (this.isLockedChild(child)) {
      event.preventDefault();
      event.stopPropagation();
      this.snack.open(
        'Complete your KYC verification to access this section.',
        'OK',
        { duration: 3500 }
      );
    }
  }

  onDropdownClick() {
    if (this.isCompact) {
      this.expandSidebar.emit();
    }
  }

  /**
   * Top-level item gating. While KYC isn't APPROVED we still let the user
   * reach Dashboard, Inbox, Help, Sign Out, and any item whose state lives
   * under /dashboard or /settings/kyc. The Settings dropdown is allowed to
   * EXPAND so the user can drill down to KYC Update — sub-items are then
   * gated individually via {@link isLockedChild}.
   */
  isLocked(item: IMenuItem): boolean {
    if (this.kycApproved) return false;
    return !this.isEssentialTopLevel(item);
  }

  isLockedChild(child: IChildItem): boolean {
    if (this.kycApproved) return false;
    return !this.isEssentialChild(child);
  }

  getTooltip(item: IMenuItem | IChildItem, isChild = false): string {
    const locked = isChild
      ? this.isLockedChild(item as IChildItem)
      : this.isLocked(item as IMenuItem);
    if (locked) {
      return 'Available after KYC approval';
    }
    if (!isChild && this.isCompact) {
      return (item as IMenuItem).name || '';
    }
    return '';
  }

  private isEssentialTopLevel(item: IMenuItem): boolean {
    const name = (item.name || '').trim().toLowerCase();
    const state = (item.state || '').trim().toLowerCase();
    if (
      name === 'dashboard' ||
      name === 'inbox' ||
      name === 'help' ||
      name === 'sign out' ||
      name === 'settings' ||
      name === 'kyc update'
    ) {
      return true;
    }
    if (
      state.startsWith('dashboard') ||
      state.startsWith('settings/kyc') ||
      state.startsWith('settings/inbox')
    ) {
      return true;
    }
    return false;
  }

  private isEssentialChild(child: IChildItem): boolean {
    const name = (child.name || '').trim().toLowerCase();
    const state = (child.state || '').trim().toLowerCase();
    return (
      name === 'kyc update' ||
      name === 'inbox' ||
      name === 'sign out' ||
      state.includes('kyc') ||
      state.includes('inbox')
    );
  }

  get orderedMenuItems(): IMenuItem[] {
    const normalizeName = (name?: string) => (name || '').split('(')[0].trim().toLowerCase();
    let items = this.menuItems;
    if (this.userRole === 'suppliers' && this.supplierMenuSource !== this.menuItems) {
      items = [...items];
      if (!items.some(item => normalizeName(item.name) === 'inventory management')) {
        items.push({ name: 'Inventory Management', type: 'dropDown', icon: 'inventory_2', action: null, sub: [
          { name: 'Products', state: '/supplier/inventory/products' },
          { name: 'Services', state: '/supplier/inventory/services' },
        ] });
      }
      // 2026-10-31: Products / Services are wired to real data; My Buyer still shows sample buyers, so it stays
      // behind SUPPLIER_PREVIEW_SCREENS_ENABLED (user decision 29 Oct: no sample screens for suppliers).
      if (SUPPLIER_PREVIEW_SCREENS_ENABLED) {
        const dataIndex = items.findIndex(item => normalizeName(item.name) === 'data management');
        if (dataIndex === -1) {
          items.push({ name: 'Data Management', type: 'dropDown', icon: 'folder_shared', action: null,
            sub: [{ name: 'My Buyer', state: '/supplier/data-management/my-buyers' }] });
        } else {
          const dataMenu = items[dataIndex];
          if (!(dataMenu.sub || []).some(child => normalizeName(child.name) === 'my buyer')) {
            items[dataIndex] = { ...dataMenu, sub: [...(dataMenu.sub || []), { name: 'My Buyer', state: '/supplier/data-management/my-buyers' }] };
          }
        }
      }
      this.supplierMenuSource = this.menuItems;
      this.supplierMenuItems = items;
    }
    if (this.userRole === 'suppliers') items = this.supplierMenuItems;
    const order = this.userRole === 'suppliers'
      ? this.menuOrder.filter(name => name !== 'data management').flatMap(name => name === 'inventory management' ? [name, 'data management'] : [name])
      : this.menuOrder;
    const itemsWithIndex = items.map((item, index) => ({ item, index }));
    itemsWithIndex.sort((a, b) => {
      const aName = normalizeName(a.item.name);
      const bName = normalizeName(b.item.name);
      const aOrder = order.indexOf(aName);
      const bOrder = order.indexOf(bName);
      const aRank = aOrder === -1 ? order.length + a.index : aOrder;
      const bRank = bOrder === -1 ? order.length + b.index : bOrder;
      return aRank - bRank;
    });
    return itemsWithIndex.map(entry => entry.item);
  }

  isSupplierPreviewMenu(item: IMenuItem): boolean {
    const name = (item.name || '').trim().toLowerCase();
    return this.userRole === 'suppliers' && (name === 'inventory management' || name === 'data management');
  }
}
